import { classNames } from "@/utils/classnames";
import React, { ForwardedRef, ReactNode, forwardRef, useId } from "react";

// ------------------------------------------------------------------
// SwitchV2 — 24×16 pill switch with a 12×12 white thumb. Two
// states (off / on) plus disabled variants of each. Rendered as
// <button role="switch"> so click + keyboard + a11y behavior come
// for free. State is controlled via `checked` + `onChange` —
// callers own the value.
// ------------------------------------------------------------------

export const SwitchV2Sizes = ["md"] as const;
export type SwitchV2Size = (typeof SwitchV2Sizes)[number];

export interface SwitchV2Props
  extends Omit<React.ComponentProps<"button">, "ref" | "children" | "onChange"> {
  size?: SwitchV2Size;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: ReactNode;
  labelClassName?: string;
}

export const SwitchV2 = forwardRef(function SwitchV2Impl(
  {
    size: _size = "md",
    checked = false,
    disabled,
    onChange,
    label,
    labelClassName,
    className,
    id: idFromProps,
    type = "button",
    ...rest
  }: SwitchV2Props,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const generatedId = useId();
  const id = idFromProps ?? `switch-${generatedId}`;
  const stateClass = disabled
    ? checked
      ? [
          // Disabled on
          "bg-cds-primary-fill-default border-cds-primary-stroke-default opacity-50",
        ].join(" ")
      : [
          // Disabled off
          "bg-cds-surface-border border-cds-surface-border",
          "dark:bg-white/[0.16] dark:border-transparent",
        ].join(" ")
    : checked
      ? [
          // Enabled on
          "bg-cds-primary-fill-default border-cds-primary-stroke-default",
        ].join(" ")
      : [
          // Enabled off
          "bg-zinc-300 border-cds-surface-border",
          "dark:bg-white/[0.16] dark:border-white/[0.04]",
        ].join(" ");

  const switchButton = (
    <button
      ref={ref}
      id={id}
      type={type}
      role={"switch"}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      data-ds-v2={""}
      className={classNames(
        "relative w-6 h-4 rounded-full border-[1px] shrink-0",
        "transition-colors duration-75",
        "focus:outline-none focus:shadow-cds-focus",
        stateClass,
        !disabled && "shadow-cds-s",
        disabled && "cursor-not-allowed",
        className,
      )}
      {...rest}
    >
      {/* Thumb — 12×12 circle, flipped left↔right based on state.
          `shadow-cds-s` gives it a faint lift off the track.
          `left-px` / `right-px` flip the anchor; no animated slide
          (matches the Figma spec — snap toggle, not a transition).
          Thumb color:
            enabled + disabled-on: white (#ffffff). Disabled-on
              gets muted by the track's opacity-50, which inherits.
            disabled-off: explicit muted thumb per Figma — zinc-400
              light / zinc-500 dark, both at 50% opacity.
          Arbitrary hex avoids dark-mode.css's `.dark .bg-white →
          zinc-900 !important` global remap. */}
      <span
        aria-hidden
        className={classNames(
          "absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-cds-s",
          checked ? "right-px" : "left-px",
          disabled && !checked ? "bg-[#a1a1aa] dark:bg-[#71717a] opacity-50" : "bg-[#ffffff]",
        )}
      />
    </button>
  );

  if (label == null) {
    return switchButton;
  }

  return (
    <label
      data-ds-v2={""}
      htmlFor={id}
      className={classNames(
        "inline-flex w-fit select-none items-center gap-cds-8",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        labelClassName,
      )}
    >
      {switchButton}
      <span className={classNames(disabled && "cursor-not-allowed")}>{label}</span>
    </label>
  );
});
